import React from 'react';
import { Button, Dropdown, Space, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  MdOutlineTextFields,
  MdOutlinePhone,
  MdOutlineEmail,
  MdOutlineRadioButtonChecked,
  MdOutlineCheckBox,
  MdOutlineArrowDropDownCircle,
  MdOutlineCalendarToday,
  MdOutlineNumbers,
  MdOutlineNotes,
} from 'react-icons/md';
import DynamicQuestionItem from './DynamicQuestionItem';
import MobileFormPreview from './MobileFormPreview';
import './event-builder.css';

export const DEFAULT_STANDARD_LAYOUT = [
  {
    _id: '67138001001a111111110001',
    questionType: 'text',
    title: '姓名',
    placeholder: '请输入您的姓名',
    isRequired: true,
    answers: [],
  },
  {
    _id: '67138002001a111111110002',
    questionType: 'phone',
    title: '手机号',
    placeholder: '请输入您的手机号',
    defaultCountryCode: '+86',
    countryCodes: [
      { label: '+86', value: '+86', _id: '3537456a6a4f36794854314a' },
      { label: '+852', value: '+852', _id: '6539413755444245734f3250' },
    ],
    isRequired: true,
    answers: [],
  },
  {
    _id: '67138001001a111111110003',
    questionType: 'text',
    title: '发廊（或公司）',
    placeholder: '请输入您所在的发廊名称或者公司名称',
    isRequired: true,
    answers: [],
  },
  {
    _id: '67138001001a111111110004',
    questionType: 'text',
    title: '邀请码',
    placeholder: '请输入您收到的展会专属邀请码',
    isRequired: true,
    code: 'INVITATION-CODE',
    answers: [],
  },
  {
    _id: '67138001001a111111110005',
    questionType: 'email',
    title: '电邮',
    placeholder: '请输入您的电子邮箱地址，便于接收通知',
    isRequired: false,
    answers: [],
  },
];

const DynamicFormBuilder = ({
  layout = [],
  onChange,
  eventTitle,
  eventSubtitle,
  posterUrl,
  startDate,
  endDate,
  location,
}) => {
  const { t } = useTranslation();

  const handleAddQuestion = (type) => {
    const newId = Date.now().toString(16) + Math.random().toString(16).slice(2, 6);

    let defaultTitle = t('New Question') || '新字段';
    let defaultPlaceholder = t('Please enter') || '请输入';
    let defaultAnswers = [];

    switch (type) {
      case 'text':
        defaultTitle = t('Short Text') || '单行文本';
        defaultPlaceholder = t('Please enter') || '请输入内容';
        break;
      case 'textarea':
        defaultTitle = t('Notes / Special Request') || '备注或需求';
        defaultPlaceholder = t('Please enter detailed requirements') || '请输入详细说明';
        break;
      case 'phone':
        defaultTitle = t('Contact Phone') || '联系电话';
        defaultPlaceholder = t('Please enter phone number') || '请输入手机号码';
        break;
      case 'email':
        defaultTitle = t('Email Address') || '电子邮箱';
        defaultPlaceholder = t('example@domain.com');
        break;
      case 'single-choice':
        defaultTitle = t('Single Choice Question') || '单项选择';
        defaultAnswers = [
          { _id: 'opt_1', answer: `${t('Option') || '选项'} 1`, isanswer: false },
          { _id: 'opt_2', answer: `${t('Option') || '选项'} 2`, isanswer: false },
        ];
        break;
      case 'multiple-choice':
        defaultTitle = t('Multiple Choice') || '多项选择 (可多选)';
        defaultAnswers = [
          { _id: 'opt_1', answer: `${t('Option') || '选项'} A`, isanswer: false },
          { _id: 'opt_2', answer: `${t('Option') || '选项'} B`, isanswer: false },
          { _id: 'opt_3', answer: `${t('Option') || '选项'} C`, isanswer: false },
        ];
        break;
      case 'select':
        defaultTitle = t('Dropdown Select') || '下拉选择';
        defaultPlaceholder = t('Please select') || '请选择';
        defaultAnswers = [
          { _id: 'opt_1', answer: `${t('Item') || '类别'} 1`, isanswer: false },
          { _id: 'opt_2', answer: `${t('Item') || '类别'} 2`, isanswer: false },
        ];
        break;
      case 'date':
        defaultTitle = t('Expected Attendance Date') || '预计到场日期';
        defaultPlaceholder = t('Select attendance date') || '选择日期';
        break;
      case 'number':
        defaultTitle = t('Number of Attendees') || '同行人数';
        defaultPlaceholder = t('e.g. 1') || '请输入数字';
        break;
      default:
        break;
    }

    const newQuestion = {
      _id: newId,
      questionType: type,
      title: defaultTitle,
      placeholder: defaultPlaceholder,
      isRequired: false,
      isFixed: false,
      answers: defaultAnswers,
    };

    onChange([...layout, newQuestion]);
  };

  const handleUpdateQuestion = (index, updatedQuestion) => {
    const updated = [...layout];
    updated[index] = updatedQuestion;
    onChange(updated);
  };

  const handleDeleteQuestion = (index) => {
    const updated = layout.filter((_, idx) => idx !== index);
    onChange(updated);
  };

  const handleMoveUp = (index) => {
    if (index <= 0) return;
    const updated = [...layout];
    const temp = updated[index - 1];
    updated[index - 1] = updated[index];
    updated[index] = temp;
    onChange(updated);
  };

  const handleMoveDown = (index) => {
    if (index >= layout.length - 1) return;
    const updated = [...layout];
    const temp = updated[index + 1];
    updated[index + 1] = updated[index];
    updated[index] = temp;
    onChange(updated);
  };

  const handleResetToDefault = () => {
    onChange(DEFAULT_STANDARD_LAYOUT);
  };

  const addMenuItems = [
    {
      type: 'group',
      label: <span style={{ fontSize: 11, color: '#94a3b8' }}>📝 {t('Basic Inputs') || '基础文本输入'}</span>,
      children: [
        {
          key: 'text',
          icon: <MdOutlineTextFields style={{ color: '#2563eb' }} />,
          label: t('Short Text') || '单行文本',
          onClick: () => handleAddQuestion('text'),
        },
        {
          key: 'textarea',
          icon: <MdOutlineNotes style={{ color: '#0284c7' }} />,
          label: t('Paragraph Text') || '多行文本',
          onClick: () => handleAddQuestion('textarea'),
        },
      ],
    },
    {
      type: 'group',
      label: <span style={{ fontSize: 11, color: '#94a3b8' }}>📱 {t('Contact Fields') || '常用联系方式'}</span>,
      children: [
        {
          key: 'phone',
          icon: <MdOutlinePhone style={{ color: '#059669' }} />,
          label: t('Phone Number') || '手机号码',
          onClick: () => handleAddQuestion('phone'),
        },
        {
          key: 'email',
          icon: <MdOutlineEmail style={{ color: '#db2777' }} />,
          label: t('Email') || '电子邮箱',
          onClick: () => handleAddQuestion('email'),
        },
      ],
    },
    {
      type: 'group',
      label: <span style={{ fontSize: 11, color: '#94a3b8' }}>🎯 {t('Choice & Selection') || '选择题型'}</span>,
      children: [
        {
          key: 'single-choice',
          icon: <MdOutlineRadioButtonChecked style={{ color: '#7c3aed' }} />,
          label: t('Single Choice (Radio)') || '单项选择',
          onClick: () => handleAddQuestion('single-choice'),
        },
        {
          key: 'multiple-choice',
          icon: <MdOutlineCheckBox style={{ color: '#9333ea' }} />,
          label: t('Multiple Choice (Checkbox)') || '多项选择',
          onClick: () => handleAddQuestion('multiple-choice'),
        },
        {
          key: 'select',
          icon: <MdOutlineArrowDropDownCircle style={{ color: '#6366f1' }} />,
          label: t('Dropdown Select') || '下拉菜单',
          onClick: () => handleAddQuestion('select'),
        },
      ],
    },
    {
      type: 'group',
      label: <span style={{ fontSize: 11, color: '#94a3b8' }}>📅 {t('Date & Number') || '日期与数字'}</span>,
      children: [
        {
          key: 'date',
          icon: <MdOutlineCalendarToday style={{ color: '#d97706' }} />,
          label: t('Date Picker') || '日期选择',
          onClick: () => handleAddQuestion('date'),
        },
        {
          key: 'number',
          icon: <MdOutlineNumbers style={{ color: '#16a34a' }} />,
          label: t('Number Input') || '数字输入',
          onClick: () => handleAddQuestion('number'),
        },
      ],
    },
  ];

  return (
    <div className="event-builder-container">
      {/* Left Column: Form Controls & Question List */}
      <div className="builder-controls-column">
        {/* Top Action Bar */}
        <div className="builder-toolbar-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>
              {t('Registration Fields Configuration') || '报名表单字段配置'}
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#2563eb',
                background: '#eff6ff',
                padding: '3px 10px',
                borderRadius: 20,
              }}
            >
              {layout.length} {t('fields total') || '项字段'}
            </span>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              {t('Configure participant information collected on H5') || '配置参会人员在手机端需填写的信息'}
            </span>
          </div>

          <Space size="middle">
            <Popconfirm
              title={t('Reset to standard fields?') || '确认恢复为标准登记表单？'}
              description={t('Current custom questions will be replaced by the default template.') || '现有字段将被标准默认模板（姓名、手机号、发廊、邀请码、邮箱）覆盖。'}
              onConfirm={handleResetToDefault}
              okText={t('Confirm') || '确认'}
              cancelText={t('Cancel') || '取消'}
            >
              <Button size="middle" icon={<ReloadOutlined />} style={{ borderRadius: 6 }}>
                {t('Reset Template') || '恢复标准模板'}
              </Button>
            </Popconfirm>

            <Dropdown menu={{ items: addMenuItems }} placement="bottomRight" trigger={['click']}>
              <Button
                type="primary"
                size="middle"
                icon={<PlusOutlined />}
                style={{
                  backgroundColor: '#E50A47',
                  borderColor: '#E50A47',
                  fontWeight: 600,
                  borderRadius: 6,
                  height: 36,
                  boxShadow: '0 2px 6px rgba(229, 10, 71, 0.25)',
                }}
              >
                {t('Add Form Field') || '添加表单字段'}
              </Button>
            </Dropdown>
          </Space>
        </div>

        {/* Questions List */}
        {layout.length === 0 ? (
          <div
            style={{
              background: '#ffffff',
              border: '2px dashed #cbd5e1',
              borderRadius: 12,
              padding: '60px 24px',
              textAlign: 'center',
            }}
          >
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 18 }}>
              {t('No form fields configured yet. Add fields or initialize with standard template.') || '暂未配置表单字段，可添加新字段或使用标准模板。'}
            </p>
            <Button
              type="primary"
              size="large"
              style={{ backgroundColor: '#E50A47', borderColor: '#E50A47', borderRadius: 6 }}
              onClick={handleResetToDefault}
            >
              {t('Load Standard Template') || '加载标准模板'}
            </Button>
          </div>
        ) : (
          layout.map((question, index) => (
            <DynamicQuestionItem
              key={question._id || index}
              question={question}
              index={index}
              total={layout.length}
              onUpdate={(updated) => handleUpdateQuestion(index, updated)}
              onDelete={() => handleDeleteQuestion(index)}
              onMoveUp={() => handleMoveUp(index)}
              onMoveDown={() => handleMoveDown(index)}
            />
          ))
        )}
      </div>

      {/* Right Column: Live Mobile Preview */}
      <div className="builder-preview-column">
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
            📱 {t('Mobile Registration Preview') || '手机报名端实时预览'}
          </span>
          <span style={{ fontSize: 11, color: '#059669', background: '#ecfdf5', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
            H5 Live
          </span>
        </div>

        <MobileFormPreview
          eventTitle={eventTitle}
          eventSubtitle={eventSubtitle}
          posterUrl={posterUrl}
          startDate={startDate}
          endDate={endDate}
          location={location}
          layout={layout}
        />
      </div>
    </div>
  );
};

export default DynamicFormBuilder;
